import { EJReportType, EJReportTypeItem } from '@cims-online/shared'
import { useAnyTrue } from 'hooks/useAnyTrue'
import {
  useConcretePlacementEjReportOptionsQuery,
  useConcreteTestEjReportOptionsQuery,
  useControlPointEjReportOptionsQuery,
  useInspectionEjReportOptionsQuery,
  useLotEjReportOptionsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { EJReportsSelectOptionsContext } from './types'
import { DefaultOptionType } from 'antd/lib/select'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { join, split, toLabeledValues as toValues } from './utils'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } =
  createStrictContext<EJReportsSelectOptionsContext>(
    'EJReportsSelectOptionsContext'
  )

export const useEJReportsSelectOptionsContext = useContextFn

const fromLabeledValue: EJReportsSelectOptionsContext['fromLabeledValue'] = (
  value
) => {
  if (!value) return undefined
  return split(value.value)
}

const fromLabeledValues: EJReportsSelectOptionsContext['fromLabeledValues'] = (
  values
) => (values ?? []).map(({ value }) => split(value))

const toLabeledValue: EJReportsSelectOptionsContext['toLabeledValue'] = (
  record
) => (record ? toValues([record])[0] : undefined)

const toLabeledValues: EJReportsSelectOptionsContext['toLabeledValues'] = (
  records
) => toValues(records ?? [])

const EJReportsSelectOptionsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()

  const options = {
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  }
  const concreteTests = useConcreteTestEjReportOptionsQuery(options)
  const concretePlacements = useConcretePlacementEjReportOptionsQuery(options)
  const lots = useLotEjReportOptionsQuery(options)
  const controlPoints = useControlPointEjReportOptionsQuery(options)
  const inspections = useInspectionEjReportOptionsQuery(options)

  const [loading] = useAnyTrue([
    concreteTests.loading,
    concretePlacements.loading,
    lots.loading,
    controlPoints.loading,
    inspections.loading,
  ])

  const optionsMap = new Map<
    EJReportTypeItem['classType'],
    DefaultOptionType[]
  >([
    [
      EJReportType.ConcreteTest.classType,
      useOptions({
        items: concreteTests.data?.concreteTests?.records,
        label: 'fieldNumber',
        value: join,
        tooltip: () => EJReportType.ConcreteTest.type,
        sort: 'fieldNumber',
      }),
    ],
    [
      EJReportType.ConcretePlacement.classType,
      useOptions({
        items: concretePlacements.data?.concretePlacements?.records,
        label: 'placementId',
        value: join,
        tooltip: () => EJReportType.ConcretePlacement.type,
        sort: 'placementId',
      }),
    ],
    [
      EJReportType.DensityLot.classType,
      useOptions({
        items: lots.data?.lots?.records,
        label: 'lotId',
        value: join,
        tooltip: () => EJReportType.DensityLot.type,
        sort: 'lotId',
      }),
    ],
    [
      EJReportType.DailyInspectionControlPoint.classType,
      useOptions({
        items: controlPoints.data?.dailyInspectionControlPoints?.records,
        label: 'controlPointId',
        value: join,
        tooltip: () => EJReportType.DailyInspectionControlPoint.type,
        sort: 'controlPointId',
      }),
    ],
    [
      EJReportType.DailyInspectionInspection.classType,
      useOptions({
        items: inspections.data?.dailyInspectionInspections?.records,
        label: 'inspectionId',
        value: join,
        tooltip: () => EJReportType.DailyInspectionInspection.type,
        sort: 'inspectionId',
      }),
    ],
  ])

  return (
    <Provider
      value={{
        optionsMap,
        selectProps: {
          mode: 'multiple',
          loading,
        },
        fromLabeledValue,
        fromLabeledValues,
        toLabeledValue,
        toLabeledValues,
      }}
    >
      {children}
    </Provider>
  )
}

export default EJReportsSelectOptionsProvider
